import { Controller } from '@hotwired/stimulus';
import { loadStripe } from '@stripe/stripe-js';
import {
  cssFontFaceSrc,
  cssVars,
} from '@inked/utilities';
import { toPx } from '@inked/utilities';
import Mustache from 'mustache';

export class StripePaymentMethodController extends Controller {
  static targets = [
    'address1',
    'address2',
    'cardType',
    'city',
    'country',
    'cvc',
    'email',
    'error',
    'errorTemplate',
    'expMonth',
    'expYear',
    'expiry',
    'last4',
    'name',
    'nonce',
    'number',
    'phone',
    'state',
    'zipcode',
  ];
  static values = { publicKey: String };

  async connect() {
    this.stripe = await loadStripe(this.publicKeyValue);

    const vars = cssVars();
    const fontFaceName = vars['--field-control-font-family'].split(',')[0].replace(/['"]/g, '').trim();
    const fontFaceWeight = vars['--field-control-font-weight'];
    const fontFaceSrc = cssFontFaceSrc(fontFaceName, fontFaceWeight);

    this.elements = this.stripe.elements({
      fonts: [
        {
          family: fontFaceName,
          src: `url(${fontFaceSrc})`,
          weight: fontFaceWeight,
        },
      ],
    });

    const style = {
      base: {
        color: vars['--field-control-color'],
        fontFamily: vars['--field-control-font-family'],
        fontSize: toPx(vars['--field-control-font-size']),
        fontSmoothing: 'antialiased',
        lineHeight: vars['--field-control-line-height'],
        '::placeholder': {
          color: vars['--field-control-color-placeholder'],
        },
      },
      invalid: {
        color: vars['--field-control-color'],
      }
    };

    this.numberElement = this.elements.create('cardNumber', { style });
    this.expiryElement = this.elements.create('cardExpiry', { style });
    this.cvcElement = this.elements.create('cardCvc', { style });

    this.mountElements();
  }

  mountElements() {
    this.numberElement.mount(this.numberTarget);
    this.expiryElement.mount(this.expiryTarget);
    this.cvcElement.mount(this.cvcTarget);

    this.numberElement.on('change', event => this.handleElementChange(event, 'number'));
    this.expiryElement.on('change', event => this.handleElementChange(event, 'expiry'));
    this.cvcElement.on('change', event => this.handleElementChange(event, 'cvc'));
  }

  handleElementChange(event, key) {
    const errorElement = this[`${key}Target`].closest('[data-field]').querySelector('[data-field-errors]');
    errorElement.classList.add('hidden');

    if (event.error) {
      errorElement.innerHTML = `<li>${event.error.message}</li>`;
      errorElement.classList.remove('hidden');
    }
  }

  // TODO: make reusable
  setFormError(error) {
    if (this.hasErrorTarget) this.errorTarget.remove();

    this.errorTemplateTarget.parentNode.insertAdjacentHTML(
      'afterbegin',
      Mustache.render(this.errorTemplateTarget.innerHTML, {
        error: error.type === 'card_error' && error.message
          ? error.message
          : 'Something went wrong saving your payment method. Please try again.',
      }),
    );
  }

  async tokenize() {
    const { error, paymentMethod } = await this.stripe.createPaymentMethod({
      type: 'card',
      card: this.numberElement,
      billing_details: {
        address: {
          line1: this.hasAddress1Target ? this.address1Target.value : null,
          line2: this.hasAddress2Target ? this.address2Target.value : null,
          city: this.hasCityTarget ? this.cityTarget.value : null,
          // TODO: confirm works with multiple countries
          state: this.hasStateTarget ? this.stateTarget.value : null,
          country: this.hasCountryTarget ? this.countryTarget.value : null,
          postal_code: this.hasZipcodeTarget ? this.zipcodeTarget.value : null,
        },
        email: this.hasEmailTarget ? this.emailTarget.value : null,
        name: this.hasNameTarget ? this.nameTarget.value : null,
        phone: this.hasPhoneTarget ? this.phoneTarget.value : null,
      },
    });

    if (error) {
      this.setFormError(error);
      return;
    }

    this.nonceTarget.value = paymentMethod.id;

    if (this.hasCardTypeTarget) {
      this.cardTypeTarget.value = paymentMethod.card.brand;
    }

    if (this.hasLast4Target) {
      this.last4Target.value = paymentMethod.card.last4;
    }

    if (this.hasExpMonthTarget) {
      this.expMonthTarget.value = paymentMethod.card.exp_month;
    }

    if (this.hasExpYearTarget) {
      this.expYearTarget.value = paymentMethod.card.exp_year;
    }

    this.element.submit();
  }

  submit(event) {
    event.preventDefault();

    this.tokenize();
  }
}
